const MAX_DIMENSION = 1536;
const MAX_BASE64_SIZE = 4.5 * 1024 * 1024;

// Sampling resolution for the transparency probe; large enough to catch small
// white details, small enough to stay off the critical path.
const PROBE_DIMENSION = 512;
// Ignore a handful of stray transparent pixels; we only care about a real
// transparent region such as a knocked-out background.
const MIN_TRANSPARENT_RATIO = 0.02;
const WHITE_CHANNEL_MIN = 240;

const toDataUrl = (base64Image) =>
  base64Image.includes(",") ? base64Image : `data:image/png;base64,${base64Image}`;

/**
 * Reports whether an image has a transparent region, and whether any of its
 * fully opaque pixels are white.
 *
 * Vision models receive transparency already composited onto white, so they
 * report a white background that does not exist in the artwork. Knowing both
 * facts lets callers drop that phantom white without touching images whose
 * white is real.
 */
export const inspectTransparency = (base64Image) =>
  new Promise((resolve) => {
    const fallback = { hasTransparency: false, opaqueHasWhite: true };
    const img = new Image();

    img.onload = () => {
      try {
        const scale = Math.min(1, PROBE_DIMENSION / Math.max(img.width, img.height));
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const { data } = ctx.getImageData(0, 0, width, height);
        let transparent = 0;
        let opaqueHasWhite = false;

        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];

          if (alpha < 16) {
            transparent += 1;
          } else if (
            alpha > 250 &&
            data[i] >= WHITE_CHANNEL_MIN &&
            data[i + 1] >= WHITE_CHANNEL_MIN &&
            data[i + 2] >= WHITE_CHANNEL_MIN
          ) {
            opaqueHasWhite = true;
          }
        }

        resolve({
          hasTransparency: transparent / (width * height) > MIN_TRANSPARENT_RATIO,
          opaqueHasWhite,
        });
      } catch (e) {
        // A tainted canvas or unreadable pixels must not break color detection.
        resolve(fallback);
      }
    };

    img.onerror = () => resolve(fallback);
    img.src = toDataUrl(base64Image);
  });

/**
 * Resize a base64 image to stay within Gemini's inline data limits.
 * Always uses PNG to preserve the alpha channel.
 * If PNG at MAX_DIMENSION is still too large, iteratively shrinks until it fits.
 * Returns a stripped base64 string (no data URL prefix).
 */
export const prepareImageForAPI = (base64Image) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const stripped = base64Image.includes(",")
        ? base64Image.split(",")[1]
        : base64Image;

      const needsResize =
        img.width > MAX_DIMENSION ||
        img.height > MAX_DIMENSION ||
        stripped.length > MAX_BASE64_SIZE;

      if (!needsResize) {
        resolve(stripped);
        return;
      }

      let scale = Math.min(
        MAX_DIMENSION / img.width,
        MAX_DIMENSION / img.height,
        1
      );

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let result;

      // Shrink progressively until the PNG fits within the size limit
      for (let attempt = 0; attempt < 6; attempt++) {
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);
        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        result = canvas.toDataURL("image/png").split(",")[1];
        if (result.length <= MAX_BASE64_SIZE) break;

        scale *= 0.75;
      }

      resolve(result);
    };

    img.onerror = () => reject(new Error("Failed to load image for resizing"));

    img.src = base64Image.includes(",")
      ? base64Image
      : `data:image/png;base64,${base64Image}`;
  });
};
